import { useCallback, useRef, useState } from 'react';

export interface LexicalNode {
  type: string;
  version: number;
  [key: string]: unknown;
}

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const TEXT_FORMAT_BOLD = 1;
const TEXT_FORMAT_ITALIC = 2;
const TEXT_FORMAT_STRIKETHROUGH = 4;
const TEXT_FORMAT_UNDERLINE = 8;
const TEXT_FORMAT_CODE = 16;

const renderNode = (node: LexicalNode): string => {
  const children = Array.isArray(node.children)
    ? (node.children as LexicalNode[]).map(renderNode).join('')
    : '';

  switch (node.type) {
    case 'paragraph': {
      const align =
        typeof node.format === 'string'
          ? ` style="text-align:${node.format}"`
          : '';

      return `<p${align}>${children}</p>`;
    }

    case 'heading': {
      const tag = typeof node.tag === 'string' ? node.tag : 'h2';

      return `<${tag}>${children}</${tag}>`;
    }

    case 'text': {
      const raw = typeof node.text === 'string' ? escapeHtml(node.text) : '';
      const format = typeof node.format === 'number' ? node.format : 0;
      let result = raw;
      if (format & TEXT_FORMAT_CODE) result = `<code>${result}</code>`;
      if (format & TEXT_FORMAT_BOLD) result = `<strong>${result}</strong>`;
      if (format & TEXT_FORMAT_ITALIC) result = `<em>${result}</em>`;
      if (format & TEXT_FORMAT_UNDERLINE) result = `<u>${result}</u>`;
      if (format & TEXT_FORMAT_STRIKETHROUGH) result = `<s>${result}</s>`;

      return result;
    }

    case 'linebreak':
      return '<br/>';

    case 'list': {
      const tag = node.listType === 'number' ? 'ol' : 'ul';

      return `<${tag}>${children}</${tag}>`;
    }

    case 'listitem':
      return `<li>${children}</li>`;

    default:
      return children;
  }
};

export const lexicalToHtml = (nodes: unknown[]): string =>
  (nodes as LexicalNode[]).map(renderNode).join('');

const SCROLL_THRESHOLD = 4;

export const useScrolledToBottom = () => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (hasScrolledToBottom) return;
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop - clientHeight <= SCROLL_THRESHOLD)
        setHasScrolledToBottom(true);
    },
    [hasScrolledToBottom]
  );

  const contentRef = useRef<HTMLDivElement>(null);

  const handleContentMount = useCallback((node: HTMLDivElement | null) => {
    contentRef.current = node;
    if (!node) return;
    if (node.scrollHeight <= node.clientHeight) setHasScrolledToBottom(true);
  }, []);

  return { hasScrolledToBottom, handleScroll, handleContentMount };
};
